import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import {
  AccessTime,
  Medication,
  CheckCircle,
  Cancel,
  Help,
  Event as EventIcon,
} from '@mui/icons-material';
import { Visit } from '../types';

interface VisitTimelineProps {
  visits: Visit[];
}

const getOutcomeColor = (outcome: string): NonNullable<ChipProps['color']> => {
  switch (outcome) {
    case 'Very Positive':
      return 'success';
    case 'Committed to Try':
      return 'info';
    case 'Positive':
      return 'primary';
    case 'Neutral':
      return 'warning';
    case 'Needs Follow-up':
      return 'error';
    default:
      return 'default';
  }
};

const getOutcomeIcon = (outcome: string) => {
  switch (outcome) {
    case 'Very Positive':
    case 'Committed to Try':
    case 'Positive':
      return <CheckCircle />;
    case 'Neutral':
      return <Help />;
    case 'Needs Follow-up':
      return <Cancel />;
    default:
      return <EventIcon />;
  }
};

const VisitItem: React.FC<{ visit: Visit }> = ({ visit }) => {
  const productColor = '#757575';

  return (
    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{ minWidth: 100 }}>
            <Typography variant="caption" color="text.secondary">
              {new Date(visit.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Chip
                label={visit.product}
                size="small"
                sx={{
                  bgcolor: productColor,
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
              <Chip
                icon={getOutcomeIcon(visit.outcome)}
                label={visit.outcome}
                size="small"
                color={getOutcomeColor(visit.outcome)}
                variant="outlined"
              />
            </Stack>
            <Stack direction="row" spacing={2} mb={1}>
              {(visit.Played_Time || visit.duration) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTime fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {visit.Played_Time ?? visit.duration ?? 0} min
                  </Typography>
                </Box>
              )}
              {visit.samplesGiven > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Medication fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {visit.samplesGiven} samples
                  </Typography>
                </Box>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {visit.notes}
            </Typography>
            {visit.nextAction && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" fontWeight="bold">
                  Next Action:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {' '}
                  {visit.nextAction}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const VisitTimeline: React.FC<VisitTimelineProps> = ({ visits }) => {
  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Last 10 Visits Timeline
      </Typography>
      <Box>
        {visits.map((visit) => (
          <VisitItem key={visit.visitId} visit={visit} />
        ))}
      </Box>
    </Box>
  );
};

